import DataGrid, { Button as GridButton, Column, LoadPanel, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import { TextBox } from 'devextreme-react/text-box';
import moment from 'moment';
import React from 'react';
import { NoteDto } from '../../../../dtos/NoteDto';
import TreeMultiSelect from '../../../shared/TreeMultiSelect';
import { NotesViewProps } from '../hooks/useNotesController';
import AddNoteDialog from '../AddNoteDialog';

// Grant-level notes have no phase/step, so they read simply as "Grant".
const locationText = (note: NoteDto): string => {
    if (note.step) return `${note.module ?? ''} › ${note.phase ?? ''} › ${note.step}`;
    if (note.phase) return `${note.module ?? ''} › ${note.phase}`;
    return 'Grant';
};

const NotesView: React.FC<NotesViewProps> = ({
    grantData,
    loading,
    notes,
    locationNodes,
    selectedLocationKeys,
    onLocationKeysChange,
    searchText,
    onSearchTextChange,
    currentUserId,
    editingNote,
    onEditNote,
    onCloseEdit,
    onNoteSaved,
    onDeleteNote,
}) => {
    // Only the author may edit/delete a note; other users' shared notes are read-only in the list.
    // UserDto carries the user key as "id" (not "userId"), which is what currentUserId holds.
    const isAuthor = (e: any): boolean => {
        const authorId = (e.row?.data as NoteDto | undefined)?.createdByUser?.id;
        return !!authorId && !!currentUserId && authorId.toLowerCase() === currentUserId.toLowerCase();
    };
    const dateRender = (cell: any) => {
        const date = cell.value ? new Date(cell.value) : null;
        return <span>{date ? moment(date).format('MM-DD-YYYY') : ''}</span>;
    };

    return (
        <div className="container-fluid panelBody" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <span style={{ fontSize: 12, color: 'rgb(118, 118, 118)' }}>Location</span>
                <TreeMultiSelect
                    nodes={locationNodes}
                    selectedKeys={selectedLocationKeys}
                    onChange={onLocationKeysChange}
                    placeholder="All locations"
                    width={280}
                />
                <TextBox
                    mode="search"
                    width={240}
                    placeholder="Search notes…"
                    value={searchText}
                    valueChangeEvent="keyup input"
                    onValueChanged={(e: any) => onSearchTextChange(e.value ?? '')}
                />
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    dataSource={notes}
                    keyExpr="id"
                    height="100%"
                    noDataText={loading ? 'Loading notes…' : 'No data'}
                    repaintChangesOnly={true}
                    rowAlternationEnabled={true}
                    showBorders={false}
                    showColumnLines={false}
                    showRowLines={true}
                    wordWrapEnabled={true}
                >
                    <LoadPanel enabled={false} />
                    <Scrolling mode="virtual" showScrollbar="always" />
                    {/* Hidden panel: the external search box drives the grid's built-in search via this text. */}
                    <SearchPanel visible={false} text={searchText} />
                    <Column
                        caption="Date"
                        cellRender={dateRender}
                        dataField="dateCreated"
                        dataType="date"
                        sortOrder="desc"
                        width={120}
                    />
                    <Column
                        caption="Location"
                        name="location"
                        calculateCellValue={locationText}
                        dataType="string"
                        width="40%"
                    />
                    <Column
                        caption="Note"
                        dataField="comment"
                        dataType="string"
                    />
                    {/* Notes are role-shared; only the author sees edit/delete on their own rows. */}
                    <Column type="buttons" width={90}>
                        <GridButton hint="Edit" icon="edit" visible={isAuthor} onClick={(e: any) => onEditNote(e.row.data)} />
                        <GridButton hint="Delete" icon="trash" visible={isAuthor} onClick={(e: any) => onDeleteNote(e.row.data)} />
                    </Column>
                </DataGrid>
            </div>

            <AddNoteDialog
                grantData={grantData}
                visible={!!editingNote}
                note={editingNote}
                onClose={onCloseEdit}
                onSaved={onNoteSaved}
            />
        </div>
    );
};

export default NotesView;
